import { Head, Link } from '@inertiajs/react'
import { PageLayout } from '@/components/landing/PageLayout'
import { PageHeader } from '@/components/landing/PageHeader'
import { Badge } from '@/components/ui/badge'
import { ArrowRight } from 'lucide-react'

interface ErrorEntry {
  type: string
  title: string
  status: number
  summary: string
}

const statusColor = (status: number) => {
  if (status >= 500) return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
  if (status >= 400) return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
  return 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20'
}

export default function ErrorsIndex({ errors }: { errors: ErrorEntry[] }) {
  return (
    <>
      <Head>
        <title>API Error Reference — KonexZero</title>
        <meta name="description" content="Every error type returned by the KonexZero API, with example responses and step-by-step remediation." />
      </Head>
      <PageLayout>
        <div className="mb-10">
          <PageHeader
            eyebrow="API Errors"
            title="Error Reference"
            description={(
              <>
                Every error response from the KonexZero API follows{' '}
                <a
                  href="https://datatracker.ietf.org/doc/html/rfc7807"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-4 hover:text-foreground"
                >
                  RFC 7807 Problem Details
                </a>
                . Each error includes a <code className="text-xs">type</code> field that links here, plus an actionable{' '}
                <code className="text-xs">suggestion</code> for resolving it.
              </>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {errors.map((err) => (
            <Link
              key={err.type}
              href={`/errors/${err.type}`}
              className="surface-card surface-card-hover group block p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-heading text-base font-semibold leading-snug">{err.title}</h3>
                <Badge variant="outline" className={statusColor(err.status)}>
                  {err.status}
                </Badge>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{err.summary}</p>
              <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground transition-colors group-hover:text-foreground">
                <code className="font-mono">{err.type}</code>
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>

        <div className="surface-card mt-12 p-6 text-sm text-muted-foreground">
          <p>
            Hitting an error not listed here? It is likely an infrastructure-level error (e.g. a 502 from the load balancer) and not
            generated by the KonexZero application itself. Check{' '}
            <Link href="/status" className="underline underline-offset-4 hover:text-foreground">
              /status
            </Link>{' '}
            or contact <a href="mailto:support@konexzero.com" className="underline underline-offset-4 hover:text-foreground">support@konexzero.com</a>.
          </p>
        </div>
      </PageLayout>
    </>
  )
}
